import { useEffect, useState } from 'react';
import { ReactNode } from 'react';
import { Flex, Layout, Row } from 'antd';
import { Header } from '@/components';
import { Sidebar } from '@/components/Sidebar';
import { useTranslation } from 'react-i18next';
import { LOCAL_EN, ROUTE } from '@/config';
import useScrollToTop from '@/hooks/useScrollToTop';
import { CURRENT_RELEASE_NOTE } from '@/constants/releaseNote';
import { useReleaseNotesStore } from '@/stores/useReleaseNotesStore';
import { ReleaseNotesPopup } from '@/components/ReleaseNotesPopup';
import { NewTicketModal } from '@/components/Header/TicketPageHeader/NewTicketModal/NewTicketModal';
import { useTicketingStore } from '@/stores';
import usePermissionSync from '@/hooks/usePermissionSync';
import usePermission from '@/hooks/usePermission';
import { useNavigate, useLocation } from 'react-router-dom';

interface MainProps {
  children: ReactNode;
}

const Main = ({ children }: MainProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = usePermission();

  useScrollToTop();
  usePermissionSync(); // Sync permissions on route changes

  const seenVersion = useReleaseNotesStore((state) => state.seenVersion);
  const setSeenVersion = useReleaseNotesStore((state) => state.setSeenVersion);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const { isModalOpen } = useTicketingStore();

  // Redirect to settings if user has no permissions
  useEffect(() => {
    if (permissions.length === 0 && location.pathname !== ROUTE.SETTINGS) {
      navigate(ROUTE.SETTINGS, { replace: true });
    }
  }, [permissions, location.pathname, navigate]);

  useEffect(() => {
    if (CURRENT_RELEASE_NOTE.version && CURRENT_RELEASE_NOTE.version !== seenVersion) {
      setShowReleaseNotes(true);
    }
  }, [seenVersion]);

  const handleCloseReleaseNotes = () => {
    setShowReleaseNotes(false);
    setSeenVersion(CURRENT_RELEASE_NOTE.version);
  };

  return (
    <>
      <ReleaseNotesPopup
        visible={showReleaseNotes}
        note={CURRENT_RELEASE_NOTE.note}
        onClose={handleCloseReleaseNotes}
      />
      <Layout className='h-screen bg-background overflow-hidden' style={{}}>
        <Flex className='w-full h-full' align='flex-start'>
          <Sidebar />
          <Flex vertical className='w-full flex-1 min-h-0 min-w-0 overflow-auto h-full'>
            <Header />
            <div className='px-4 sm:px-10 pt-6 pb-6 flex-1 flex flex-col min-h-0'>
              {children}
            </div>
          </Flex>
        </Flex>
        <NewTicketModal isOpen={isModalOpen} />
      </Layout>
    </>
  );
};

export default Main;
